import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, AdminAccountOpeningRequest } from '../api/admin';
import { providersApi, Provider, Currency, currenciesApi } from '../api/providers';
import { CheckCircle, XCircle, Clock, RefreshCw, X } from 'lucide-react';
import { formatPublicIdDisplay } from '../utils/formatPublicId';
import { validateBankingIdentifiers } from '../utils/bankingValidation';

const STATUS_STYLES: Record<string, string> = {
  PENDING:  'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

interface ApproveForm {
  providerId: string;
  currencyId: string;       // pre-filled from request, read-only
  accountName: string;
  accountNumber: string;
  iban: string;
  bankName: string;
  branchCode: string;
  bankCode: string;
  bankLocation: string;
  bankAddress: string;
  swiftCode: string;
  intermediaryBankName: string;
  intermediaryBankSwift: string;
  notes: string;
}

const EMPTY_APPROVE: ApproveForm = {
  providerId: '', currencyId: '', accountName: '', accountNumber: '', iban: '',
  bankName: '', branchCode: '', bankCode: '', bankLocation: '',
  bankAddress: '', swiftCode: '', intermediaryBankName: '', intermediaryBankSwift: '', notes: '',
};

interface Props {
  onPendingCountChange?: (count: number) => void;
}

export default function AccountRequestsPanel({ onPendingCountChange }: Props) {
  const [requests, setRequests]       = useState<AdminAccountOpeningRequest[]>([]);
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]             = useState('');
  const [filterStatus, setFilterStatus] = useState('PENDING');

  // Reject modal
  const [rejectId, setRejectId]       = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  // Approve modal
  const [approveRequest, setApproveRequest] = useState<AdminAccountOpeningRequest | null>(null);
  const [approveForm, setApproveForm] = useState<ApproveForm>(EMPTY_APPROVE);
  const [providers, setProviders]     = useState<Provider[]>([]);
  const [, setFilteredCurrencies] = useState<Currency[]>([]);
  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [formError, setFormError]     = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await adminApi.getAccountOpeningRequests();
      const data = resp.data || [];
      setRequests(data);
      onPendingCountChange?.(data.filter((x: AdminAccountOpeningRequest) => x.status?.toUpperCase() === 'PENDING').length);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [onPendingCountChange]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // ── Reject ────────────────────────────────────────────────────────────────
  const openReject = (id: string) => { setRejectId(id); setRejectNotes(''); };
  const closeReject = () => { setRejectId(null); setRejectNotes(''); };

  const handleReject = async () => {
    if (!rejectId || !rejectNotes.trim()) return;
    try {
      setActionLoading(true);
      await adminApi.reviewAccountOpeningRequest(rejectId, 'REJECTED', rejectNotes);
      closeReject();
      await fetchRequests();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Approve ───────────────────────────────────────────────────────────────
  const openApprove = async (req: AdminAccountOpeningRequest) => {
    setApproveRequest(req);
    setApproveForm({ ...EMPTY_APPROVE, currencyId: req.requestedCurrency });
    setFieldErrors(new Set());
    setFormError('');
    try {
      const [pResp, cResp] = await Promise.all([
        providersApi.getAll(),
        currenciesApi.getAll(),
      ]);
      setProviders(pResp);
      setAllCurrencies(cResp);
      setFilteredCurrencies([]);
    } catch {
      setFormError('Failed to load providers/currencies');
    }
  };

  const closeApprove = () => {
    setApproveRequest(null);
    setApproveForm(EMPTY_APPROVE);
    setFieldErrors(new Set());
    setFormError('');
    setFilteredCurrencies([]);
  };

  const handleProviderChange = (providerId: string) => {
    setApproveForm(f => ({ ...f, providerId }));
    clearFieldError('Provider');
    if (providerId) {
      const p = providers.find(p => p.id === providerId);
      if (p) {
        const ids = new Set(p.currencies.map(c => c.currencyId));
        setFilteredCurrencies(allCurrencies.filter(c => ids.has(c.id)));
      }
    } else {
      setFilteredCurrencies([]);
    }
  };

  const clearFieldError = (f: string) => {
    setFieldErrors(prev => { const n = new Set(prev); n.delete(f); return n; });
  };

  const fieldClass = (label: string) => {
    const base = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
    return fieldErrors.has(label) ? `${base} border-red-400 bg-red-50` : `${base} border-gray-300`;
  };

  const validateApprove = () => {
    const required: { key: keyof ApproveForm; label: string }[] = [
      { key: 'providerId',   label: 'Provider' },
      { key: 'accountName',  label: 'Account Name' },
      { key: 'bankName',     label: 'Bank Name' },
      { key: 'bankLocation', label: 'Bank Location' },
      { key: 'bankAddress',  label: 'Bank Address' },
      { key: 'swiftCode',    label: 'SWIFT Code' },
    ];
    const empty = required.filter(r => !approveForm[r.key]?.toString().trim());
    const identifierError = validateBankingIdentifiers(approveForm.accountNumber, approveForm.iban);
    const errors = new Set(empty.map(e => e.label));

    if (identifierError) {
      errors.add('Account Number');
      errors.add('IBAN');
    }

    if (errors.size > 0) {
      setFieldErrors(errors);
      if (identifierError && empty.length === 0) {
        setFormError(identifierError);
      } else if (identifierError) {
        setFormError(`${identifierError}. Also fill in: ${empty.map(e => e.label).join(', ')}`);
      } else {
        setFormError(`Please fill: ${empty.map(e => e.label).join(', ')}`);
      }
      return false;
    }
    return true;
  };

  const handleApprove = async () => {
    if (!approveRequest || !validateApprove()) return;
    try {
      setActionLoading(true);
      await adminApi.reviewAccountOpeningRequest(
        approveRequest.id,
        'APPROVED',
        approveForm.notes || undefined,
        {
          providerId:    approveForm.providerId,
          accountName:   approveForm.accountName,
          accountNumber: approveForm.accountNumber || undefined,
          iban:          approveForm.iban || undefined,
          bankName:      approveForm.bankName,
          branchCode:    approveForm.branchCode || undefined,
          bankCode:      approveForm.bankCode || undefined,
          bankLocation:  approveForm.bankLocation,
          bankAddress:   approveForm.bankAddress,
          swiftCode:     approveForm.swiftCode,
          intermediaryBankName:  approveForm.intermediaryBankName || undefined,
          intermediaryBankSwift: approveForm.intermediaryBankSwift || undefined,
        },
      );
      closeApprove();
      await fetchRequests();
    } catch (err: any) {
      setFormError(err.response?.data?.error || err.response?.data || err.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = requests.filter(r =>
    filterStatus === 'ALL' ? true : r.status?.toUpperCase() === filterStatus
  );
  const pendingCount = requests.filter(r => r.status?.toUpperCase() === 'PENDING').length;

  return (
    <>
    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900">Account Opening Requests</h3>
          {pendingCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
              {pendingCount} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="ALL">All</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <button
            onClick={fetchRequests}
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review Notes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-10 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">No requests found</td></tr>
            ) : filtered.map(req => {
              const statusKey = req.status?.toUpperCase();
              const isPending = statusKey === 'PENDING';
              return (
                <tr key={req.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{req.clientFullName || '—'}</div>
                    {req.clientPublicId && <div className="text-xs font-mono text-blue-500">{formatPublicIdDisplay(req.clientPublicId)}</div>}
                    <div className="text-xs text-gray-400">{req.clientEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      {req.requestedCurrency}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[statusKey] || 'bg-gray-100 text-gray-700'}`}>
                      {statusKey === 'PENDING'  && <Clock className="w-3 h-3" />}
                      {statusKey === 'APPROVED' && <CheckCircle className="w-3 h-3" />}
                      {statusKey === 'REJECTED' && <XCircle className="w-3 h-3" />}
                      {req.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                    {new Date(req.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-500 max-w-[140px] truncate">{req.reviewNotes || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isPending ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => openApprove(req)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => openReject(req.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {req.reviewedAt ? new Date(req.reviewedAt).toLocaleDateString() : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* ── Reject Modal ───────────────────────────────────────────────────── */}
    {rejectId && (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Reject Request</h4>
            <button onClick={closeReject} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Please provide a reason for rejection. The client will see this comment.</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectNotes}
              onChange={e => setRejectNotes(e.target.value)}
              rows={4}
              placeholder="e.g. Incomplete documentation, currency not supported in your region…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={closeReject} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={actionLoading || !rejectNotes.trim()}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
            >
              {actionLoading ? 'Rejecting…' : 'Confirm Rejection'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Approve Modal ──────────────────────────────────────────────────── */}
    {approveRequest && (
      <div className="fixed inset-0 bg-black bg-opacity-40 overflow-y-auto z-50">
        <div className="min-h-screen flex items-start justify-center py-10 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="flex items-start justify-between p-6 border-b border-gray-200">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Approve Account Request</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Client: <span className="font-medium">{approveRequest.clientFullName}</span>
                  {approveRequest.clientPublicId && <span className="ml-2 font-mono text-blue-500">{formatPublicIdDisplay(approveRequest.clientPublicId)}</span>}
                  {' · '}Currency: <span className="font-semibold text-blue-700">{approveRequest.requestedCurrency}</span>
                </p>
              </div>
              <button onClick={closeApprove} className="text-gray-400 hover:text-gray-600 mt-1"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6">
              {formError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{formError}</div>
              )}

              <div className="space-y-4">
                {/* Provider */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('Provider') ? 'text-red-600' : 'text-gray-700'}`}>
                    Provider <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={approveForm.providerId}
                    onChange={e => handleProviderChange(e.target.value)}
                    className={fieldClass('Provider')}
                  >
                    <option value="">Select provider</option>
                    {providers.map(p => <option key={p.id} value={p.id!}>{p.name}</option>)}
                  </select>
                </div>

                {/* Currency — read-only from request */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <input
                    type="text"
                    value={approveRequest.requestedCurrency}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <hr className="border-gray-200" />
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Bank account details</p>

                {/* Account Name */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('Account Name') ? 'text-red-600' : 'text-gray-700'}`}>
                    Account Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={approveForm.accountName}
                    onChange={e => { setApproveForm(f => ({...f, accountName: e.target.value})); clearFieldError('Account Name'); }}
                    className={fieldClass('Account Name')} placeholder="Enter account name" />
                </div>

                {/* Account Number */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('Account Number') ? 'text-red-600' : 'text-gray-700'}`}>
                    Account Number
                  </label>
                  <input type="text" value={approveForm.accountNumber}
                    onChange={e => { setApproveForm(f => ({...f, accountNumber: e.target.value})); clearFieldError('Account Number'); clearFieldError('IBAN'); }}
                    className={fieldClass('Account Number')} placeholder="Enter account number" />
                </div>

                {/* IBAN */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('IBAN') ? 'text-red-600' : 'text-gray-700'}`}>
                    IBAN
                  </label>
                  <input type="text" value={approveForm.iban}
                    onChange={e => { setApproveForm(f => ({...f, iban: e.target.value.toUpperCase()})); clearFieldError('Account Number'); clearFieldError('IBAN'); }}
                    className={fieldClass('IBAN')} placeholder="e.g. BH13SGBD79621400000128" />
                </div>
                <p className="text-xs text-gray-500 -mt-2">Fill in Account Number, IBAN, or both.</p>

                {/* Bank Name */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('Bank Name') ? 'text-red-600' : 'text-gray-700'}`}>
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={approveForm.bankName}
                    onChange={e => { setApproveForm(f => ({...f, bankName: e.target.value})); clearFieldError('Bank Name'); }}
                    className={fieldClass('Bank Name')} placeholder="Enter bank name" />
                </div>

                {/* Branch Code + Bank Code */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                    <input type="text" value={approveForm.branchCode}
                      onChange={e => setApproveForm(f => ({...f, branchCode: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Code</label>
                    <input type="text" value={approveForm.bankCode}
                      onChange={e => setApproveForm(f => ({...f, bankCode: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional" />
                  </div>
                </div>

                {/* Bank Location */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('Bank Location') ? 'text-red-600' : 'text-gray-700'}`}>
                    Bank Location <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={approveForm.bankLocation}
                    onChange={e => { setApproveForm(f => ({...f, bankLocation: e.target.value})); clearFieldError('Bank Location'); }}
                    className={fieldClass('Bank Location')} placeholder="City, Country" />
                </div>

                {/* Bank Address */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('Bank Address') ? 'text-red-600' : 'text-gray-700'}`}>
                    Bank Address <span className="text-red-500">*</span>
                  </label>
                  <textarea rows={2} value={approveForm.bankAddress}
                    onChange={e => { setApproveForm(f => ({...f, bankAddress: e.target.value})); clearFieldError('Bank Address'); }}
                    className={fieldClass('Bank Address')} placeholder="Full bank address" />
                </div>

                {/* SWIFT Code */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${fieldErrors.has('SWIFT Code') ? 'text-red-600' : 'text-gray-700'}`}>
                    SWIFT Code <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={approveForm.swiftCode}
                    onChange={e => { setApproveForm(f => ({...f, swiftCode: e.target.value.toUpperCase()})); clearFieldError('SWIFT Code'); }}
                    className={fieldClass('SWIFT Code')} placeholder="e.g. DEUTDEDB" maxLength={11} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intermediary Bank Name</label>
                  <input type="text" value={approveForm.intermediaryBankName}
                    onChange={e => setApproveForm(f => ({...f, intermediaryBankName: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intermediary Bank SWIFT</label>
                  <input type="text" value={approveForm.intermediaryBankSwift}
                    onChange={e => setApproveForm(f => ({...f, intermediaryBankSwift: e.target.value.toUpperCase()}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional" maxLength={11} />
                </div>

                {/* Internal notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes (optional)</label>
                  <input type="text" value={approveForm.notes}
                    onChange={e => setApproveForm(f => ({...f, notes: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional internal comment" />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={closeApprove} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="flex-1 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                >
                  {actionLoading ? 'Creating account…' : '✓ Approve & Create Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
